import { useState } from "react";
import { Loader2, Fuel, Receipt, Wrench, CalendarOff, ClipboardCheck, Check, X } from "lucide-react";
import toast from "react-hot-toast";
import { useDriverRequests } from "../hooks/useDriverRequests";

const TYPE_META = {
  fuel:        { label: "Fuel Log",    Icon: Fuel,           color: "#2563EB", bg: "#EFF6FF" },
  expense:     { label: "Expense",     Icon: Receipt,        color: "#D97706", bg: "#FFFBEB" },
  maintenance: { label: "Maintenance", Icon: Wrench,         color: "#4F46E5", bg: "#EEF2FF" },
  leave:       { label: "Leave",       Icon: CalendarOff,    color: "#6B7280", bg: "#F3F4F6" },
  inspection:  { label: "Inspection",  Icon: ClipboardCheck, color: "#DC2626", bg: "#FEF2F2" },
};

const EXPENSE_CATEGORIES = { toll: "Toll", repair: "Repair", insurance_premium: "Insurance Premium", salary: "Driver Salary", other: "Other" };

const FILTERS = [
  { key: "pending",  label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all",      label: "All" },
];

function summarizePayload(type, p) {
  if (type === "fuel") return `${p.liters ? `${p.liters}L` : ""}${p.cost ? ` · ₹${p.cost}` : ""}${p.fuelStation ? ` · ${p.fuelStation}` : ""}`.trim() || "Fuel fill-up";
  if (type === "expense") return `${EXPENSE_CATEGORIES[p.category] || p.category} · ₹${p.amount}${p.notes ? ` — ${p.notes}` : ""}`;
  if (type === "maintenance") return `${p.serviceType}${p.notes ? ` — ${p.notes}` : ""}`;
  if (type === "leave") return `${p.startDate} → ${p.endDate}${p.reason ? ` — ${p.reason}` : ""}`;
  if (type === "inspection") {
    const flagged = (p.checklist || []).filter(c => c.status !== "ok");
    return flagged.length ? flagged.map(f => `${f.item}${f.note ? ` (${f.note})` : ""}`).join(", ") : "All clear";
  }
  return "";
}

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
}

function RequestRow({ r, onApprove, onReject }) {
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const { label, Icon, color, bg } = TYPE_META[r.type];

  async function handleApprove() {
    setBusy(true);
    try { await onApprove(r.id); toast.success("Request approved"); }
    catch (err) { toast.error(err.response?.data?.error || "Failed to approve"); }
    finally { setBusy(false); }
  }
  async function handleReject() {
    setBusy(true);
    try { await onReject(r.id, note || null); toast.success("Request rejected"); }
    catch (err) { toast.error(err.response?.data?.error || "Failed to reject"); }
    finally { setBusy(false); setRejecting(false); }
  }

  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
        <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: bg, color, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={16} />
        </div>
        <div style={{ flex: "1 1 240px", minWidth: 180 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{r.driverName}</span>
            <span style={{ fontSize: 10.5, fontWeight: 700, color, background: bg, borderRadius: 5, padding: "2px 8px" }}>{label}</span>
            {r.vehiclePlate && <span style={{ fontSize: 11.5, color: "#9CA3AF" }}>{r.vehiclePlate}</span>}
          </div>
          <div style={{ fontSize: 13, color: "#374151", marginTop: 4 }}>{summarizePayload(r.type, r.payload)}</div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>{fmtDate(r.createdAt)}</div>
          {r.status !== "pending" && r.reviewerNote && (
            <div style={{ fontSize: 12, color: r.status === "rejected" ? "#DC2626" : "#16A34A", marginTop: 6, background: r.status === "rejected" ? "#FEF2F2" : "#F0FDF4", borderRadius: 6, padding: "6px 10px" }}>
              Note: {r.reviewerNote}
            </div>
          )}
        </div>

        {r.status === "pending" && !rejecting && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button onClick={handleApprove} disabled={busy} title="Approve" style={{ display: "flex", alignItems: "center", gap: 5, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 7, padding: "0 12px", height: 32, cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "#16A34A" }}>
              {busy ? <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} /> : <Check size={12} />} Approve
            </button>
            <button onClick={() => setRejecting(true)} disabled={busy} title="Reject" style={{ display: "flex", alignItems: "center", gap: 5, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 7, padding: "0 12px", height: 32, cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "#DC2626" }}>
              <X size={12} /> Reject
            </button>
          </div>
        )}
        {r.status !== "pending" && (
          <span style={{
            fontSize: 10.5, fontWeight: 700, borderRadius: 5, padding: "3px 9px", flexShrink: 0,
            color: r.status === "approved" ? "#16A34A" : "#DC2626",
            background: r.status === "approved" ? "#F0FDF4" : "#FEF2F2",
          }}>{r.status === "approved" ? "Approved" : "Rejected"}</span>
        )}
      </div>

      {rejecting && (
        <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
          <input
            autoFocus
            value={note} onChange={e => setNote(e.target.value)}
            placeholder="Reason for rejection (optional)…"
            style={{ flex: 1, height: 34, padding: "0 11px", background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 7, fontSize: 12.5, color: "#111827", outline: "none" }}
          />
          <button onClick={handleReject} disabled={busy} style={{ height: 34, borderRadius: 7, border: "none", background: "#DC2626", color: "#fff", fontSize: 12.5, fontWeight: 600, padding: "0 14px", cursor: "pointer" }}>Confirm</button>
          <button onClick={() => setRejecting(false)} style={{ height: 34, borderRadius: 7, border: "1px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 12.5, padding: "0 14px", cursor: "pointer" }}>Cancel</button>
        </div>
      )}
    </div>
  );
}

export default function RequestsPanel() {
  const [status, setStatus] = useState("pending");
  const { requests, loading, approve, reject } = useDriverRequests(status);

  return (
    <main style={{ flex: 1, overflow: "auto", background: "#F9FAFB" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 24px" }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>Driver Requests</div>
          <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>Fuel logs, expenses, maintenance issues, leave, and pre-trip inspections submitted by your drivers</div>
        </div>

        <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setStatus(f.key)}
              style={{
                padding: "6px 14px", borderRadius: 7, border: "none", cursor: "pointer",
                fontSize: 12.5, fontWeight: 600,
                background: status === f.key ? "#111827" : "#F3F4F6",
                color: status === f.key ? "#fff" : "#6B7280",
              }}
            >{f.label}</button>
          ))}
        </div>

        {loading && (
          <div style={{ padding: 60, textAlign: "center", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading requests…
          </div>
        )}

        {!loading && requests.length === 0 && (
          <div style={{ background: "#fff", border: "1px dashed #D1D5DB", borderRadius: 14, padding: "48px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
              {status === "pending" ? "No pending requests" : `No ${status} requests`}
            </div>
            <div style={{ fontSize: 13, color: "#9CA3AF" }}>
              {status === "pending" ? "Fuel logs, expenses, maintenance issues, leave and inspection requests from your drivers will show up here." : "Nothing to show for this filter."}
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {requests.map(r => (
            <RequestRow key={r.id} r={r} onApprove={approve} onReject={reject} />
          ))}
        </div>
      </div>
    </main>
  );
}
